import * as React from 'react';
import useHasChanged from './useHasChanged';
/**
 * A custom hook that returns a debounced value and a function to update it programmatically.
 *
 * @example
 * ```
 * const [debounced, updateValue] = useDebounceAdvanced(query);
 * ```
 *
 * @example
 * ```
 * const [debounced, updateValue, immediateUpdate] = useDebounceAdvanced(query, 500);
 * immediateUpdate('set without debounce delay');
 * ```
 * @export
 */
const useDebounce = (value, timeout, returnUpdateFunction) => {
    if (!timeout)
        timeout = 300;
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    const hasValueChanged = useHasChanged(value);
    const timerRef = React.useRef(null);
    React.useEffect(() => {
        if (hasValueChanged)
            waitAndUpdate(value);
        return () => {
            if (timerRef === null || timerRef === void 0 ? void 0 : timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [hasValueChanged, value, timeout]);
    function waitAndUpdate(val) {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setDebouncedValue(val);
        }, timeout);
    }
    function immediateUpdate(val) {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        setDebouncedValue(val);
    }
    return (returnUpdateFunction) ? [debouncedValue, waitAndUpdate, immediateUpdate] : debouncedValue;
};
export default useDebounce;
